import React from 'react';
import Form from 'components/form';
import AnswerOptions from 'routes/surface/components/node-modal-form/components/questionnaire-node-modal/components/questionnaire-node-form/components/answer-options';
import If from 'components/if-statement';
import { useTranslation } from 'i18n';
import { Header } from 'routes/surface/components/node-modal-form/style';
import {
  Container,
  AnswerSection,
  NameTextInput,
  DisabledName,
  QuestionInput,
  QuestionIcon,
  CloseIcon,
} from './style';
import { H2 } from 'components/typography';
import { object, string } from 'yup';
import { isMobile } from 'react-device-detect';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import { ErrorMessage, TextMessage } from 'components/textautosize/style';
import { NodeModalInput } from 'types';
import { SurfaceQuestionnaireNode } from 'graphql/types';
import useCanCreateOrEditSurfaceNode from 'routes/surface/components/node-modal-form/hooks/use-can-create-or-edit-surface-node';
import { ANSWER_TYPE } from './components/answer-option-label';

export interface QuestionnaireNodeFormProps {
  onSubmit: Function;
  onCancel: Function;
  data: NodeModalInput<SurfaceQuestionnaireNode>;
  saveNodeInCache: (node: any) => any;
}

const defaultProps = {};

function createValidationSchema(t) {
  return function () {
    return object().shape({
      name: string().required(t('name is required')),
      question: string().required(t('question is required')),
      answerType: string().required(t('answer type is required')),
    });
  };
}

function QuestionnaireNodeForm({
  data,
  onSubmit,
  ...props
}: QuestionnaireNodeFormProps) {
  const { t } = useTranslation();

  const validationSchema = React.useMemo(createValidationSchema(t), []);

  const {
    canCreateOrEdit,
    isUpdate,
    cannotEdit,
  } = useCanCreateOrEditSurfaceNode(data);

  const submitLabel = isUpdate
    ? t('Update questionnaire')
    : t('Insert questionnaire');

  async function formSubmit(values, actions) {
    const response = await onSubmit(values, actions);

    if (response.isFailure) {
      // Error will be handled in onSubmit
      return;
    }

    const node = response.getField('data');

    props.saveNodeInCache(node);
  }

  function clearQuestion(form) {
    return function () {
      if (cannotEdit) {
        return;
      }

      form.setFieldValue('question', '');
    };
  }

  function isNameInputAutoFocused() {
    if (isUpdate) {
      return isMobile ? false : true;
    }

    return true;
  }

  return (
    <Form
      initialValues={data}
      validationSchema={validationSchema}
      onSubmit={formSubmit}>
      {(form) => {
        const answerTypeError = form.errors['answerType'];
        const isFreeText = ![
          ANSWER_TYPE.OPEN,
          ANSWER_TYPE.YES,
          ANSWER_TYPE.NO,
          'yes',
        ].includes(form.values.answerType);
        const hasName = form.values.name != form.values.question;
        return (
          <>
            <Header>
              <Form.Button
                label={t('Close')}
                onClick={props.onCancel}
                secondary
              />
              {canCreateOrEdit && (
                <Form.SubmitButton
                  label={submitLabel}
                  onClick={form.handleSubmit}
                />
              )}
            </Header>
            <Container>
              <If condition={cannotEdit}>
                <If.Then>
                  <DisabledName>{form.values.name}</DisabledName>
                </If.Then>
                <If.Else>
                  <NameTextInput
                    name="name"
                    placeholder={t('Name of questionnaire node')}
                    autoFocus={isNameInputAutoFocused()}
                  />
                </If.Else>
              </If>
              <If condition={hasName}>
                <If.Then>
                  <QuestionInput
                    iconLeft={<QuestionIcon />}
                    iconRight={
                      cannotEdit === false && (
                        <CloseIcon onClick={clearQuestion} />
                      )
                    }
                    placeholder={t('Type your question here')}
                    disabled={cannotEdit}
                    name="question"
                  />
                </If.Then>
              </If>

              <If condition={isFreeText}>
                <If.Then>
                  {/* <TextMessage>{form.values.answerType}</TextMessage> */}

                  <QuestionInput
                    // iconLeft={<QuestionIcon />}
                    // iconRight={
                    //   cannotEdit === false && <CloseIcon onClick={clearQuestion} />
                    // }
                    placeholder={t('Type your answer here')}
                    disabled={cannotEdit}
                    name="answerType"
                  />
                </If.Then>
                <If.Else>
                  <AnswerSection $invalid={answerTypeError}>
                    <H2>{t('How would you answer the question?')}</H2>

                    <AnswerOptions name="answerType" disabled={cannotEdit} />
                  </AnswerSection>
                </If.Else>
              </If>

              {/* {isFreeText }
              {} */}

              <If condition={answerTypeError}>
                <ErrorMessage>{answerTypeError}</ErrorMessage>
              </If>
            </Container>
          </>
        );
      }}
    </Form>
  );
}

QuestionnaireNodeForm.defaultProps = defaultProps;

export default QuestionnaireNodeForm;
